import type { TerminalFocusOwner } from "../../lib/terminalFocus";

/**
 * Where a terminal keyboard command lands.
 *
 * A thread shows terminals in two places — the drawer below the conversation
 * and a right panel surface — and the same shortcut means different things
 * depending on which one holds focus. This states which one a command is for,
 * and whether the drawer has to be opened before the command can run.
 *
 * Performing the command stays with the Supported Client Surface; only the
 * choice of destination lives here.
 */
export type ThreadTerminalCommand =
  | "terminal.split"
  | "terminal.splitVertical"
  | "terminal.new"
  | "terminal.close";

const THREAD_TERMINAL_COMMANDS: ReadonlySet<string> = new Set([
  "terminal.split",
  "terminal.splitVertical",
  "terminal.new",
  "terminal.close",
]);

/** Whether a resolved shortcut command is one this module places. */
export function isThreadTerminalCommand(command: string): command is ThreadTerminalCommand {
  return THREAD_TERMINAL_COMMANDS.has(command);
}

export type ThreadTerminalTarget = "right-panel" | "drawer";

export interface ThreadTerminalPlacement {
  readonly target: ThreadTerminalTarget;
  /** The drawer is closed and this command needs it open to have an effect. */
  readonly openDrawerFirst: boolean;
}

/**
 * A command follows focus into the right panel, and otherwise goes to the
 * drawer, opening it first when it is closed.
 *
 * Closing is the exception in both directions. It only follows focus into the
 * right panel when that panel is actually showing a terminal, because a
 * focused panel of some other kind has no terminal to close; and it never
 * opens the drawer, because opening a drawer in order to close a terminal in
 * it would leave the thread further from where the shortcut asked to be. With
 * the drawer closed there is nothing to close, so the command does not land
 * anywhere.
 */
export function placeThreadTerminalCommand(input: {
  readonly command: ThreadTerminalCommand;
  readonly focusOwner: TerminalFocusOwner | null;
  readonly drawerOpen: boolean;
  readonly rightPanelSurfaceKind?: string | null | undefined;
}): ThreadTerminalPlacement | null {
  const focusedOnRightPanel = input.focusOwner === "right-panel";

  if (input.command === "terminal.close") {
    if (focusedOnRightPanel && input.rightPanelSurfaceKind === "terminal") {
      return { target: "right-panel", openDrawerFirst: false };
    }
    if (!input.drawerOpen) return null;
    return { target: "drawer", openDrawerFirst: false };
  }

  if (focusedOnRightPanel) {
    return { target: "right-panel", openDrawerFirst: false };
  }
  return { target: "drawer", openDrawerFirst: !input.drawerOpen };
}
